// =============================================================================
// TOLOnews Data Architecture Types
// Based on TOLOnews_Data_Architecture_Specification.md
// =============================================================================

// =============================================================================
// ENUMS - Match PostgreSQL enum types
// =============================================================================

// Publication status (IPTC standard)
export type PubStatus = 'usable' | 'withheld' | 'canceled';

// Content/item types
export type ItemType = 'text' | 'video' | 'audio' | 'picture' | 'graphic' | 'composite' | 'live_blog';

// Platforms
export type Platform = 'tv' | 'radio' | 'web' | 'social' | 'all';

// Desks
export type Desk = 'tv' | 'radio' | 'web' | 'social' | 'international';

// Breaking news levels
export type BreakingLevel = 'flash' | 'urgent' | 'developing';

// Workflow states (full lifecycle)
export type WorkflowStatus =
  | 'idea' | 'pitched' | 'assigned'                                    // Planning
  | 'draft' | 'ai_review' | 'submitted' | 'editing' | 'in_review' | 'needs_revision' | 'approved' | 'scheduled'  // Production (ai_review is journalist-side AI check before formal submission)
  | 'published' | 'updated'                                            // Published
  | 'killed' | 'withdrawn' | 'archived';                               // Terminal

// Planning item status
export type PlanningStatus = 'planned' | 'confirming' | 'assigned' | 'in_progress' | 'covered' | 'canceled';

// Version types (wire service pattern)
export type VersionType = 'initial' | 'take' | 'update' | 'correction' | 'writethru';

// Signal types (IPTC processing instruction)
export type SignalType = 'update' | 'correction';

// Translation status
export type TranslationStatus = 'not_started' | 'ai_draft' | 'in_review' | 'approved' | 'published';

// Translation method
export type TranslationMethod = 'human' | 'machine' | 'machine_post_edited';

// Adaptation level
export type AdaptationLevel = 'verbatim' | 'light_edit' | 'localized' | 'regional_adaptation' | 'independent';

// Collection types
export type CollectionType = 'rundown' | 'web_feed' | 'social_queue' | 'radio_lineup';

// Ordering modes for collections
export type OrderingMode = 'time_sequenced' | 'priority_based' | 'manual';

// Collection status
export type CollectionStatus = 'planning' | 'locked' | 'on_air' | 'completed' | 'archived';

// Broadcast item types
export type BroadcastItemType = 'VO' | 'SOT' | 'PKG' | 'LIVE' | 'BREAK' | 'TEASE' | 'BUMP';

// Approval stages
export type ApprovalStage = 'copy_edit' | 'section_review' | 'senior_approval' | 'legal';

// Approval decisions
export type ApprovalDecision = 'approved' | 'rejected' | 'needs_revision' | 'pending';

// Component types (lower thirds, etc.)
export type ComponentType = 'lower_third' | 'quote_card' | 'locator' | 'headline_graphic';

// MOS statuses
export type MosStatus = 'sent' | 'acknowledged' | 'aired';
export type MosItemStatus = 'pending' | 'ready' | 'cued' | 'on_air' | 'done';

// User roles
export type UserRole = 'journalist' | 'editor' | 'senior_editor' | 'admin' | 'superuser' | 'suspended' | 'producer' | 'supervisor';


// =============================================================================
// LANGUAGE - BCP47 codes
// =============================================================================

export type LanguageCode = 'fa-AF' | 'ps-AF' | 'eng';

// =============================================================================
// ORGANIZATION DEFAULTS - Change these to configure system-wide defaults
// =============================================================================

/** Default language for new content (change to 'ps-AF' for Pashto or 'eng' for English) */
export const DEFAULT_LANGUAGE: LanguageCode = 'fa-AF';

/** Default platform for new assignments */
export const DEFAULT_PLATFORM: Platform = 'tv';

/** All supported languages in display order */
export const SUPPORTED_LANGUAGES: LanguageCode[] = ['fa-AF', 'ps-AF', 'eng'];

/** All supported platforms in display order */
export const SUPPORTED_PLATFORMS: Platform[] = ['tv', 'radio', 'web', 'social'];

// =============================================================================

export const LANGUAGE_LABELS: Record<LanguageCode, string> = {
  'fa-AF': 'دری',      // Dari
  'ps-AF': 'پښتو',     // Pashto
  'eng': 'English',
};

export const LANGUAGE_NAMES: Record<LanguageCode, string> = {
  'fa-AF': 'Dari',
  'ps-AF': 'Pashto',
  'eng': 'English',
};

// Legacy enum for backwards compatibility
export enum Language {
  ENGLISH = 'english',
  DARI = 'dari',
  PASHTO = 'pashto',
}

// Map legacy Language enum to BCP47 codes
export const LEGACY_TO_BCP47: Record<Language, LanguageCode> = {
  [Language.ENGLISH]: 'eng',
  [Language.DARI]: 'fa-AF',
  [Language.PASHTO]: 'ps-AF',
};

export const BCP47_TO_LEGACY: Record<LanguageCode, Language> = {
  'eng': Language.ENGLISH,
  'fa-AF': Language.DARI,
  'ps-AF': Language.PASHTO,
};


// =============================================================================
// USERS
// =============================================================================

export interface User {
  id: string;
  email: string;
  full_name: string;
  role: UserRole;
  desk?: Desk;
  avatar_url?: string;
  is_active: boolean;
  created_at: string;
}


// =============================================================================
// STORIES (The Hub)
// =============================================================================

export interface Story {
  id: string;
  guid: string;                        // urn:newsml:tolonews.com:YYYYMMDD:SLUG
  slug: string;                        // Human-readable: AFG-KABUL-ELECTION-2025
  canonical_language: LanguageCode;
  urgency: number;                     // 1-9 IPTC scale, 1=highest
  pub_status: PubStatus;
  is_breaking: boolean;
  breaking_level?: BreakingLevel;
  subjects: IPTCSubject[];             // IPTC Media Topics with QCodes
  created_at: string;
  created_by?: string;
  updated_at: string;
  archived_at?: string;
}

export interface IPTCSubject {
  qcode: string;                       // e.g., "medtop:20000538"
  name: string;                        // English name
  name_fa?: string;                    // Dari name
  name_ps?: string;                    // Pashto name
}

// Helper to generate story GUID
export function generateStoryGuid(slug: string): string {
  const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  return `urn:newsml:tolonews.com:${date}:${slug}`;
}


// =============================================================================
// PLANNING ITEMS (The Intent)
// =============================================================================

export interface PlanningItem {
  id: string;
  story_id: string;
  title?: string;                      // Assignment title: "TV Package (Dari)"
  assigned_desk: Desk;
  platform: Platform;                  // Target platform: tv, radio, web, social
  language: LanguageCode;              // Target language: fa-AF, ps-AF, eng
  item_type: ItemType;
  assigned_to?: string;
  due_date?: string;
  priority: number;                    // 1-100
  status: PlanningStatus;
  notes?: string;
  created_at: string;
  created_by?: string;
  promoted_to_content_at?: string;
}

export interface PlanningItemWithUser extends PlanningItem {
  assigned_user?: User;                // Populated when fetching with joins
  content_item?: ContentItem;          // The content item created from this planning item
  story?: Story;                       // Populated when fetching with joins
}


// =============================================================================
// CONTENT ITEMS (The Assets/Spokes)
// =============================================================================

export interface ContentItem {
  id: string;
  guid: string;
  story_id: string;
  planning_id?: string;
  language: LanguageCode;
  is_canonical: boolean;
  derived_from_content_id?: string;
  item_type: ItemType;
  platform: Platform;
  current_version_id?: string;
  published_version_id?: string;
  workflow_status: WorkflowStatus;

  // Breaking news fast-track
  fast_track_authorized_by?: string;
  fast_track_authorized_at?: string;
  skipped_approvals?: string[];
  post_publication_review_required: boolean;
  post_publication_reviewed_by?: string;
  post_publication_reviewed_at?: string;

  // MOS integration
  mos_item_id?: string;
  mos_obj_type?: string;
  mos_status?: MosStatus;
  mos_last_update?: string;

  // Assignment
  assigned_to?: string;

  // Timestamps
  created_at: string;
  created_by?: string;
  published_at?: string;
  archived_at?: string;
  updated_at: string;
}

// Helper to generate content item GUID
export function generateContentGuid(storySlug: string, language: LanguageCode, platform: Platform): string {
  const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  return `urn:newsml:tolonews.com:${date}:${storySlug}-${language}-${platform}`;
}


// =============================================================================
// CONTENT VERSIONS (Immutable Audit Log)
// =============================================================================

export interface ContentVersion {
  id: string;
  content_item_id: string;
  version_number: number;
  version_type: VersionType;
  signal?: SignalType;
  headline?: string;
  summary?: string;
  body_text?: string;
  body_ninjs: NinjsPayload;            // Full ninjs-compliant JSON
  duration_seconds?: number;
  editorial_note?: string;             // Required for corrections
  created_by: string;
  created_at: string;
  is_auto_save: boolean;
}

// ninjs (News in JSON) payload structure
export interface NinjsPayload {
  uri?: string;
  type?: ItemType;
  versioncreated?: string;
  firstcreated?: string;
  language?: LanguageCode;
  urgency?: number;
  pubstatus?: PubStatus;
  headline?: string;
  slugline?: string;
  byline?: string;
  located?: string;
  body_text?: string;
  body_html?: string;
  description_text?: string;
  subjects?: IPTCSubject[];
  associations?: Record<string, NinjsAssociation>;
  renditions?: Record<string, NinjsRendition>;

  // TV/Radio specific fields
  duration?: number;
  anchor_intro?: string;
  outcue?: string;
  segments?: ContentSegment[];

  // Custom TOLOnews fields
  graphics_required?: string[];
  video_slugs?: string[];
  seo_title?: string;
  seo_description?: string;
  keywords?: string[];
  featured_image_url?: string;
  featured_image_caption?: string;
  category?: string;
  tags?: string[];
}

export interface NinjsAssociation {
  uri?: string;
  type?: ItemType;
  headline?: string;
}

export interface NinjsRendition {
  href?: string;
  contenttype?: string;
  width?: number;
  height?: number;
}

// Content segment (for TV/Radio scripts)
export interface ContentSegment {
  id: string;
  type: 'anchor' | 'vo' | 'sot' | 'pkg_intro' | 'standup' | 'graphics' | 'actuality' | 'wrap' | 'voicer';
  text: string;
  duration: number;
  video_notes?: string;
  sot_speaker?: string;
  sot_title?: string;
  outcue?: string;
}


// =============================================================================
// TRANSLATIONS
// =============================================================================

export interface Translation {
  id: string;
  source_content_id: string;
  target_content_id: string;
  source_language: LanguageCode;
  target_language: LanguageCode;
  translation_status: TranslationStatus;
  translation_method?: TranslationMethod;
  mt_engine?: string;
  confidence_score?: number;           // 0.00-1.00
  adaptation_level: AdaptationLevel;
  ai_disclosure_required: boolean;
  translator_id?: string;
  reviewer_id?: string;
  source_last_modified?: string;
  is_stale: boolean;
  created_at: string;
  completed_at?: string;
}


// =============================================================================
// COLLECTIONS (Rundowns + Digital Queues)
// =============================================================================

export interface Collection {
  id: string;
  slug: string;                        // e.g., "6PM-NEWS-2025-01-16"
  type: CollectionType;
  ordering_mode: OrderingMode;
  platform: Platform;
  channel?: string;                    // e.g., "TOLO-TV"
  target_datetime?: string;            // Air time for rundowns
  scheduled_duration?: string;         // ISO 8601 duration
  status: CollectionStatus;

  // MOS integration
  mos_ro_id?: string;
  mos_ro_slug?: string;
  ncs_id: string;

  // Locking
  locked_by?: string;
  locked_at?: string;

  // Audit
  created_at: string;
  created_by?: string;
  updated_at: string;
}


// =============================================================================
// COLLECTION ITEMS
// =============================================================================

export interface CollectionItem {
  id: string;
  collection_id: string;
  content_item_id: string;
  story_id?: string;                   // Denormalized

  // Ordering
  sequence_order?: number;

  // Time-sequenced (broadcast)
  planned_start?: string;
  planned_duration?: string;           // ISO 8601 duration
  back_time?: string;
  segment_code?: string;               // e.g., "A1", "B3"
  item_type_broadcast?: BroadcastItemType;

  // Priority-based (digital)
  priority?: number;                   // 1-100
  is_pinned: boolean;
  pinned_until?: string;
  pinned_by?: string;
  is_featured: boolean;

  // MOS integration
  mos_item_status?: MosItemStatus;
  mos_item_channel?: string;
  last_mos_update?: string;

  // Status
  status: 'scheduled' | 'active' | 'complete' | 'killed';

  // Audit
  created_at: string;
  added_by?: string;
}


// =============================================================================
// COMPONENTS (Lower Thirds, Quote Cards)
// =============================================================================

export interface Component {
  id: string;
  story_id: string;
  component_type: ComponentType;
  canonical_language: LanguageCode;
  translations: ComponentTranslations;
  timecode_in?: string;                // ISO 8601 duration
  timecode_out?: string;
  is_active: boolean;
  graphics_template_id?: string;
  graphics_data?: Record<string, unknown>;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface ComponentTranslations {
  [language: string]: {
    line1: string;
    line2?: string;
  };
}


// =============================================================================
// COORDINATED RELEASES
// =============================================================================

export interface CoordinatedRelease {
  id: string;
  story_id: string;
  go_live_time: string;
  status: 'scheduled' | 'pending_verification' | 'released' | 'canceled';
  notes?: string;
  created_by?: string;
  created_at: string;
}

export interface ReleaseTarget {
  id: string;
  release_id: string;
  collection_id?: string;
  content_item_id?: string;
  platform: Platform;
  status: 'scheduled' | 'pending_verification' | 'live' | 'failed';
  verified_by?: string;
  verified_at?: string;
  actual_live_time?: string;
}


// =============================================================================
// WORKFLOW
// =============================================================================

export interface WorkflowDefinition {
  id: string;
  name: string;
  description?: string;
  content_type?: ItemType;
  is_active: boolean;
  is_default: boolean;
  created_at: string;
}

export interface WorkflowState {
  id: string;
  workflow_id: string;
  state_name: string;
  state_type: 'planning' | 'production' | 'published' | 'terminal';
  display_order: number;
  is_initial: boolean;
  is_final: boolean;
  color: string;
}

export interface WorkflowTransition {
  id: string;
  workflow_id: string;
  from_state: string;
  to_state: string;
  transition_name: string;
  required_roles?: string[];
  auto_transition: boolean;
  creates_notification: boolean;
}

export interface WorkflowHistory {
  id: string;
  content_item_id: string;
  from_status?: string;
  to_status: string;
  transition_name?: string;
  triggered_by?: string;
  triggered_at: string;
  notes?: string;
  skipped_reason?: string;
}


// =============================================================================
// APPROVALS
// =============================================================================

export interface Approval {
  id: string;
  content_item_id: string;
  content_version_id?: string;
  approval_stage: ApprovalStage;
  approver_id?: string;
  decision: ApprovalDecision;
  submitted_at: string;
  decided_at?: string;
  comments?: string;
  revision_requested_fields?: string[];
}


// =============================================================================
// EDITORIAL COMMENTS
// =============================================================================

export interface EditorialComment {
  id: string;
  content_item_id: string;
  content_version_id?: string;
  author_id: string;
  comment_text: string;
  field_reference?: string;
  position_start?: number;
  position_end?: number;
  is_resolved: boolean;
  resolved_by?: string;
  resolved_at?: string;
  created_at: string;
}


// =============================================================================
// UPDATE NOTIFICATIONS
// =============================================================================

export interface UpdateNotification {
  id: string;
  source_content_id: string;
  source_version?: number;
  target_type: 'translation' | 'output';
  target_id: string;
  notification_created: string;
  acknowledged_at?: string;
  acknowledged_by?: string;
  action_taken?: 'updated' | 'no_action_needed' | 'deferred';
}


// =============================================================================
// AUDIT LOG
// =============================================================================

export interface AuditLog {
  id: string;
  user_id?: string;
  action: string;
  entity_type?: string;
  entity_id?: string;
  old_values?: Record<string, unknown>;
  new_values?: Record<string, unknown>;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
}


// =============================================================================
// TERMINOLOGY
// =============================================================================

export interface Terminology {
  id: string;
  term_en?: string;
  term_fa?: string;
  term_ps?: string;
  context?: string;
  notes?: string;
  created_at: string;
}


// =============================================================================
// API RESPONSE TYPES
// =============================================================================

export interface StoryWithContent extends Story {
  planning_items?: PlanningItemWithUser[];
  content_items?: ContentItemWithVersion[];
  components?: Component[];
}

export interface ContentItemWithVersion extends ContentItem {
  current_version?: ContentVersion;
  published_version?: ContentVersion;
  translation?: Translation;
  assigned_user?: User;  // Populated when fetching with joins
}

export interface CollectionWithItems extends Collection {
  items?: CollectionItemWithContent[];
}

export interface CollectionItemWithContent extends CollectionItem {
  content_item?: ContentItemWithVersion;
  story?: Story;
}


// =============================================================================
// AI SERVICE TYPES
// =============================================================================

export interface JournalistAgentRequest {
  headline: string;
  deck: string;
  body: string;
  language: LanguageCode;
  feedback?: string;
}

export interface JournalistAgentResponse {
  headline: string;
  deck: string;
  body: string;
}

export interface CriticAgentRequest {
  headline: string;
  deck: string;
  body: string;
  language: LanguageCode;
  isTranslation?: boolean;
}

export interface CriticAgentResponse {
  issues: EditorIssue[];
}

export interface EditorIssue {
  id?: string;
  category: 'ai_fixable' | 'human_required' | 'custom';
  text: string;
  suggestion?: string;
  applied?: boolean;
}

export interface TranslatorAgentRequest {
  headline: string;
  deck: string;
  body: string;
  sourceLanguage: LanguageCode;
  targetLanguage: LanguageCode;
}

export interface TranslatorAgentResponse {
  headline: string;
  deck: string;
  body: string;
}

export interface SocialAgentRequest {
  headline: string;
  deck: string;
  body: string;
  language: LanguageCode;
}

export interface SocialAgentResponse {
  posts: SocialPost[];
}

export interface SocialPost {
  platform: 'twitter' | 'facebook' | 'telegram' | 'instagram' | 'tiktok';
  text: string;
  character_count: number;
  hashtags?: string[];
}


// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

// Get workflow status category
export function getWorkflowCategory(status: WorkflowStatus): 'planning' | 'production' | 'published' | 'terminal' {
  const planning: WorkflowStatus[] = ['idea', 'pitched', 'assigned'];
  const production: WorkflowStatus[] = ['draft', 'submitted', 'editing', 'in_review', 'needs_revision', 'approved', 'scheduled'];
  const published: WorkflowStatus[] = ['published', 'updated'];

  if (planning.includes(status)) return 'planning';
  if (production.includes(status)) return 'production';
  if (published.includes(status)) return 'published';
  return 'terminal';
}

// Get urgency label
export function getUrgencyLabel(urgency: number): string {
  if (urgency === 1) return 'Flash';
  if (urgency === 2) return 'Urgent';
  if (urgency <= 4) return 'High Priority';
  if (urgency === 5) return 'Normal';
  if (urgency <= 7) return 'Low Priority';
  return 'Evergreen';
}

// Estimate duration from word count
export function estimateDuration(wordCount: number, itemType?: ItemType): number {
  const wordsPerSecond = 2.5; // Average speaking pace
  const baseTime = wordCount / wordsPerSecond;

  switch (itemType) {
    case 'video':
      return baseTime + 30; // Add time for package elements
    case 'audio':
      return baseTime + 15;
    default:
      return baseTime;
  }
}

// Format duration in MM:SS
export function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

// Parse ISO 8601 duration to seconds
export function parseDuration(duration: string): number {
  // Simple parser for HH:MM:SS format
  const parts = duration.split(':').map(Number);
  if (parts.length === 3) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }
  if (parts.length === 2) {
    return parts[0] * 60 + parts[1];
  }
  return parts[0] || 0;
}


// =============================================================================
// BROADCAST FORMAT LABELS
// =============================================================================

export const BROADCAST_ITEM_LABELS: Record<BroadcastItemType, string> = {
  'VO': 'Voice Over',
  'SOT': 'Sound on Tape',
  'PKG': 'Package',
  'LIVE': 'Live Shot',
  'BREAK': 'Break',
  'TEASE': 'Tease',
  'BUMP': 'Bump',
};


// =============================================================================
// WORKFLOW STATUS LABELS & COLORS
// =============================================================================

export const WORKFLOW_STATUS_LABELS: Record<WorkflowStatus, string> = {
  'idea': 'Idea',
  'pitched': 'Pitched',
  'assigned': 'Assigned',
  'draft': 'Draft',
  'ai_review': 'AI Review',
  'submitted': 'Submitted',
  'editing': 'Editing',
  'in_review': 'In Review',
  'needs_revision': 'Needs Revision',
  'approved': 'Approved',
  'scheduled': 'Scheduled',
  'published': 'Published',
  'updated': 'Updated',
  'killed': 'Killed',
  'withdrawn': 'Withdrawn',
  'archived': 'Archived',
};

export const WORKFLOW_STATUS_COLORS: Record<WorkflowStatus, string> = {
  'idea': '#9CA3AF',
  'pitched': '#60A5FA',
  'assigned': '#34D399',
  'draft': '#FBBF24',
  'ai_review': '#8B5CF6',  // Purple for AI review (journalist's domain)
  'submitted': '#F97316',
  'editing': '#A78BFA',
  'in_review': '#EC4899',
  'needs_revision': '#EF4444',
  'approved': '#10B981',
  'scheduled': '#06B6D4',
  'published': '#22C55E',
  'updated': '#14B8A6',
  'killed': '#6B7280',
  'withdrawn': '#9CA3AF',
  'archived': '#D1D5DB',
};
